import React, { useState, useEffect, useCallback } from 'react';
import { dataApi } from '../services/dataService';
import {
  shopTodayKey,
  shopWeekStartKey,
  shopMonthStartKey,
  shopYearStartKey,
  saleOnOrAfter,
  saleInDateRange,
  saleQuantity,
  saleAmount
} from '../utils/shopDate';

interface Stall {
  stall_id: number;
  stall_name: string;
  user_id: number;
  location: string;
  manager: string;
  status: string;
}

interface Sale {
  sale_id: number;
  item_name: string;
  item_id?: number;
  stall_id?: number | null;
  quantity_sold: number;
  unit_price: number;
  total_amount: number;
  sale_type: string;
  date_time: string;
  recorded_by: number;
  recorded_by_name: string;
  stall_name: string;
  buying_price?: number;
  cash_amount?: number | null;
  mobile_amount?: number | null;
}

interface Analytics {
  totalRevenue: number;
  cumulativeRevenue: number;
  /** Revenue minus what the goods sold in this period cost to buy. */
  grossProfit: number;
  totalSales: number;
  totalUnits: number;
  averageSale: number;
  topSellingItems: Array<{ item_name: string; total_sold: number; revenue: number }>;
  userPerformance: Array<{ user_name: string; sales: number; revenue: number }>;
  dailySales: Array<{ date: string; sales: number; revenue: number }>;
  commissionData: Array<{ user_name: string; sales: number; commission: number }>;
}

const AdminDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [stalls, setStalls] = useState<Stall[]>([]);
  const [allSales, setAllSales] = useState<Sale[]>([]);
  // Sales inside the selected period. The stall tiles read this so they agree
  // with the headline cards instead of always showing lifetime totals.
  const [periodSales, setPeriodSales] = useState<Sale[]>([]);
  const [inventoryResponse, setInventoryResponse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('today');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [todaySales, setTodaySales] = useState(0);

  const fetchAdminData = useCallback(async () => {
    try {
      // Fetch stalls
      const stallsResponse = await dataApi.getStalls();
      setStalls(stallsResponse.stalls || []);

      // Fetch inventory for stock value calculation
      const inventoryResp = await dataApi.getInventory();
      const sortedItems = [...(inventoryResp.items || [])].sort((a, b) =>
        a.item_name.localeCompare(b.item_name, undefined, { sensitivity: 'base' })
      );
      setInventoryResponse({ ...inventoryResp, items: sortedItems });

      // Fetch recent sales
      const recentSalesResponse = await dataApi.getSales();
      const allSales = recentSalesResponse.sales || [];

      // Sort sales by date (newest first) for timely display
      const sortedSales = [...allSales].sort((a, b) => {
        const dateA = new Date(a.date_time).getTime();
        const dateB = new Date(b.date_time).getTime();
        return dateB - dateA;
      });

      setAllSales(sortedSales);

      // Filter by Nairobi business day so "Today" matches what the shop sold
      // that calendar day, not the browser's midnight or the UTC date.
      const todayKey = shopTodayKey();
      let filteredSales = allSales;

      if (selectedPeriod === 'today') {
        filteredSales = allSales.filter(sale => saleInDateRange(sale.date_time, todayKey, todayKey));
      } else if (selectedPeriod === 'week') {
        const startKey = shopWeekStartKey(todayKey);
        filteredSales = allSales.filter(sale => saleOnOrAfter(sale.date_time, startKey));
      } else if (selectedPeriod === 'month') {
        const startKey = shopMonthStartKey(todayKey);
        filteredSales = allSales.filter(sale => saleOnOrAfter(sale.date_time, startKey));
      } else if (selectedPeriod === 'year') {
        const startKey = shopYearStartKey(todayKey);
        filteredSales = allSales.filter(sale => saleOnOrAfter(sale.date_time, startKey));
      } else if (selectedPeriod === 'custom' && startDate && endDate) {
        filteredSales = allSales.filter(sale => saleInDateRange(sale.date_time, startDate, endDate));
      }

      setPeriodSales(filteredSales);

      const totalRevenue = filteredSales.reduce((sum: number, sale: any) => sum + saleAmount(sale), 0);
      // Revenue for every sale ever recorded. This used to be shown as "Total
      // Revenue" next to period-filtered cards, so the headline number barely
      // moved when the period changed. It is now a clearly labelled all-time
      // total alongside the period figure.
      const cumulativeRevenue = allSales.reduce(
        (sum: number, sale: any) => sum + saleAmount(sale),
        0
      );

      // Gross profit for the selected period: what the goods sold actually
      // fetched, minus what those same goods cost to buy. The old figure
      // subtracted the value of unsold stock still on the shelves from
      // all-time revenue, which is not a profit for any period.
      const buyingPriceByItem = new Map<number, number>();
      sortedItems.forEach((item: any) => {
        buyingPriceByItem.set(Number(item.item_id), Number(item.buying_price) || 0);
      });

      const costOfGoodsSold = filteredSales.reduce((sum: number, sale: any) => {
        const qty = saleQuantity(sale);
        return sum + qty * (buyingPriceByItem.get(Number(sale.item_id)) || 0);
      }, 0);
      const grossProfit = totalRevenue - costOfGoodsSold;

      const itemSalesMap = new Map<string, { total_sold: number; revenue: number }>();
      filteredSales.forEach((sale: any) => {
        const existing = itemSalesMap.get(sale.item_name) || { total_sold: 0, revenue: 0 };
        itemSalesMap.set(sale.item_name, {
          total_sold: existing.total_sold + saleQuantity(sale),
          revenue: existing.revenue + saleAmount(sale)
        });
      });

      const topSellingItems = Array.from(itemSalesMap.entries())
        .map(([item_name, data]) => ({ item_name, ...data }))
        .sort((a, b) => b.total_sold - a.total_sold)
        .slice(0, 5);

      const userSalesMap = new Map<string, { sales: number; revenue: number }>();
      filteredSales.forEach((sale: any) => {
        const userName = sale.recorded_by_name || 'Unknown';
        const existing = userSalesMap.get(userName) || { sales: 0, revenue: 0 };
        userSalesMap.set(userName, {
          sales: existing.sales + 1,
          revenue: existing.revenue + saleAmount(sale)
        });
      });

      const userPerformance = Array.from(userSalesMap.entries())
        .map(([user_name, data]) => ({ user_name, ...data }))
        .sort((a, b) => b.revenue - a.revenue);

      const analyticsData: Analytics = {
        totalRevenue: totalRevenue,
        cumulativeRevenue: cumulativeRevenue,
        grossProfit: grossProfit,
        totalSales: filteredSales.length,
        // Guarded: one row with a missing quantity used to turn this card into NaN.
        totalUnits: filteredSales.reduce((sum: number, sale: any) => sum + saleQuantity(sale), 0),
        averageSale: filteredSales.length > 0 ? totalRevenue / filteredSales.length : 0,
        topSellingItems: topSellingItems,
        userPerformance: userPerformance,
        dailySales: [],
        commissionData:         userPerformance.map(user => ({
          user_name: user.user_name,
          sales: user.sales,
          commission: user.revenue * 0.05
        }))
      };

      setAnalytics(analyticsData);
      const todaySalesData = allSales.filter(sale => saleInDateRange(sale.date_time, todayKey, todayKey));
      setTodaySales(todaySalesData.reduce((sum, sale) => sum + saleAmount(sale), 0));

    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, startDate, endDate]);

  useEffect(() => {
    fetchAdminData();
    
    // Add event listeners for real-time updates (fast path)
    const handleSalesUpdate = () => {
      console.log('🔄 Real-time sales update detected');
      fetchAdminData();
    };
    
    const handleInventoryUpdate = () => {
      console.log('🔄 Real-time inventory update detected');
      fetchAdminData();
    };
    
    window.addEventListener('sales-updated', handleSalesUpdate);
    window.addEventListener('inventory-updated', handleInventoryUpdate);
    
    // Fallback polling (increased to 30s to reduce unnecessary requests)
    const interval = setInterval(() => {
      console.log('📡 Polling admin data (fallback)');
      fetchAdminData();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('sales-updated', handleSalesUpdate);
      window.removeEventListener('inventory-updated', handleInventoryUpdate);
    };
  }, [fetchAdminData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const getStallSalesSummary = (stall: Stall) => {
    // Prefer stall_id so a missing/mismatched display name cannot hide revenue.
    const stallSales = periodSales.filter(sale =>
      sale.stall_id != null
        ? Number(sale.stall_id) === Number(stall.stall_id)
        : sale.stall_name === stall.stall_name
    );
    const revenue = stallSales.reduce((sum, sale) => sum + saleAmount(sale), 0);
    const count = stallSales.length;

    const contributorMap = new Map<string, number>();
    stallSales.forEach(sale => {
      if (sale.recorded_by_name) {
        const currentAmount = contributorMap.get(sale.recorded_by_name) || 0;
        contributorMap.set(sale.recorded_by_name, currentAmount + saleAmount(sale));
      }
    });

    const contributors = Array.from(contributorMap.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount);

    return { revenue, count, contributors };
  };

  const items = inventoryResponse?.items || [];
  const totalStockValue = items.reduce((sum: number, item: any) => {
    // Current Stock in system: central stock + distributed stock (unsold at stalls)
    // Note: current_stock already accounts for distributions and sales deductions
    // So we only count current_stock + unsold items at stalls
    const centralStock = Number(item.current_stock) || 0;
    // Only STALL sales come out of distributed stock; central sales are
    // already deducted from current_stock (avoids double counting).
    const itemStallSales = allSales.filter(s =>
      (s.item_id === item.item_id || s.item_name === item.item_name) &&
      s.stall_id !== null && s.stall_id !== undefined
    );
    const totalSoldAtStalls = itemStallSales.reduce((sum, s: any) => sum + (s.quantity_sold || 0), 0);
    // Unsold at stalls = allocated - sold at stalls
    const distributedLive = Math.max(0, (Number(item.total_allocated) || 0) - totalSoldAtStalls);
    
    // Total unsold stock = central (already in current_stock) + unsold at stalls
    const unsoldStock = centralStock + distributedLive;
    return sum + (unsoldStock * (Number(item.buying_price) || 0));
  }, 0);

  const grossProfit = analytics?.grossProfit || 0;
  const profitTone = grossProfit > 0 ? 'text-green-600' : grossProfit < 0 ? 'text-red-600' : 'text-orange-500';

  // The revenue and profit cards follow the period selector, so say which
  // period they are showing rather than leaving them looking like all-time.
  const periodLabel = selectedPeriod === 'today'
    ? 'Today'
    : selectedPeriod === 'week'
      ? 'This Week'
      : selectedPeriod === 'month'
        ? 'This Month'
        : selectedPeriod === 'year'
          ? 'This Year'
          : startDate && endDate ? 'Selected Dates' : 'All Time';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading admin dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-primary text-white p-4 sm:p-6 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate">Admin Dashboard</h1>
            <p className="text-sm sm:text-base text-blue-100 mt-1 truncate">System Overview & Analytics - {new Date().toLocaleDateString()}</p>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {['today', 'week', 'month', 'year', 'custom'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedPeriod === period
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1).replace('custom', 'Custom Range').replace('week', 'This Week').replace('month', 'This Month').replace('year', 'This Year')}
                </button>
              ))}
            </div>

            {selectedPeriod === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 bg-white p-3 rounded-lg border-2 border-white shadow-md">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-gray-700 uppercase">From:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 text-sm font-medium border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-gray-700 uppercase">To:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 text-sm font-medium border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-6">
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-lg border-l-4 border-yellow-500 relative overflow-hidden">
          <div className="absolute left-2 bottom-2">
            <div className="flex flex-col items-center bg-white border border-gray-200 rounded-lg shadow-sm" style={{ width: '40px' }}>
              <div className="bg-red-500 text-white text-[7px] font-bold py-0.5 w-full text-center rounded-t">
                {new Date().toLocaleString('en', { month: 'short' }).toUpperCase()}
              </div>
              <div className="text-xl font-bold text-gray-800 py-1">{new Date().getDate()}</div>
            </div>
          </div>
          <div className="flex flex-col relative z-10 pl-12">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Today's Sales</h3>
            <p className="text-lg font-bold text-yellow-600 break-words">{formatCurrency(todaySales)}</p>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-lg border-l-4 border-blue-500 relative overflow-hidden">
          <div className="absolute left-2 bottom-2 text-4xl opacity-100">💰</div>
          <div className="flex flex-col relative z-10 pl-12">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue · {periodLabel}</h3>
            <p className="text-lg font-bold text-blue-600 break-words">{formatCurrency(analytics?.totalRevenue || 0)}</p>
            <p className="text-[10px] text-gray-400 break-words">
              All time: {formatCurrency(analytics?.cumulativeRevenue || 0)}
            </p>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-lg border-l-4 border-purple-500 relative overflow-hidden">
          <div className="absolute left-2 bottom-2 text-4xl opacity-100">📦</div>
          <div className="flex flex-col relative z-10 pl-12">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Units Sold · {periodLabel}</h3>
            <p className="text-lg font-bold text-purple-600 break-words">{analytics?.totalUnits || 0}</p>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-lg border-l-4 border-indigo-500 relative overflow-hidden">
          <div className="absolute left-2 bottom-2 text-4xl opacity-100">🏦</div>
          <div className="flex flex-col relative z-10 pl-12">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Value</h3>
            <p className="text-base font-bold text-indigo-600 break-words">{formatCurrency(totalStockValue)}</p>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow-lg border-l-4 border-orange-500 relative overflow-hidden">
          <div className="absolute left-2 bottom-2 text-4xl opacity-100">📈</div>
          <div className="flex flex-col relative z-10 pl-12">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Profit · {periodLabel}</h3>
            <p className={`text-lg font-bold ${profitTone} break-words`}>{formatCurrency(grossProfit)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-xl font-bold text-gray-900">User Performance</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {analytics?.userPerformance.map((user, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <h3 className="font-medium text-gray-900">{user.user_name}</h3>
                    <p className="text-xs text-gray-500">{user.sales} sales recorded</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{formatCurrency(user.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-xl font-bold text-gray-900">Top Selling Items</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {analytics?.topSellingItems.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <h3 className="font-medium text-gray-900">{item.item_name}</h3>
                    <p className="text-xs text-gray-500">{item.total_sold} units sold</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-blue-600">{formatCurrency(item.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-bold text-gray-900">Stalls Overview</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stalls.map((stall) => {
              const summary = getStallSalesSummary(stall);
              return (
                <div key={stall.stall_id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-900">{stall.stall_name}</h3>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${stall.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {stall.status}
                    </span>
                  </div>
                  <div className="space-y-1 mb-3">
                    <p className="text-xs text-gray-600 flex items-center gap-1">👤 <span className="font-medium">{stall.manager}</span></p>
                    <p className="text-xs text-gray-600 flex items-center gap-1">📍 <span>{stall.location}</span></p>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Sales · {periodLabel}</span>
                      <span>{summary.count} sales</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600 mb-2">{formatCurrency(summary.revenue)}</p>
                    {summary.contributors.length > 0 && (
                      <div className="space-y-1">
                        {summary.contributors.slice(0, 3).map((contributor, idx) => (
                          <div key={idx} className="flex justify-between items-center text-[10px] bg-white border border-gray-100 px-2 py-1 rounded">
                            <span className="text-gray-700">{contributor.name}</span>
                            <span className="font-bold text-gray-900">{formatCurrency(contributor.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
